/**
 * RegistrationJourney — the day-1 "where is my trust in the process" strip.
 *
 * A quiet, four-step status rail that sits directly under the dashboard hero.
 * It answers the question a freshly-onboarded client actually has on day one —
 * "what has happened, and what happens next?" — from data already fetched on
 * the dashboard:
 *
 *   1. Documents on file  — how many documents the trust has (useTrustDocuments)
 *   2. Master registration: the Letter of Authority's date and the IT number,
 *                           and nothing else (never the deed date)
 *   3. Assets into trust  — n of m assets registered vs. still transferring
 *                           (useTrustAssets), with a link to the Assets tab
 *   4. FICA review        — a calm time-to-review line (never alarmist)
 *
 * Design language: reuses the app's `.status-pill` convention — green (`active`)
 * for a completed step, brand (`in-progress`) for one still moving, grey
 * (`not-connected`) for one not started, and `overdue` (negative) only for a
 * genuinely lapsed FICA review. Copy is sentence-case and jargon-free.
 */

import { Link } from '@tanstack/react-router';
import {
  useTrustDocuments,
  useTrustAssets,
} from '../../lib/api';
import { Skeleton } from '../ui/skeleton';
import { PENDING_REGISTRATION } from '../../lib/format/registration';

type StepState = 'done' | 'in-progress' | 'todo' | 'overdue';

const PILL_CLASS: Record<StepState, string> = {
  done: 'status-pill active',
  'in-progress': 'status-pill in-progress',
  todo: 'status-pill not-connected',
  overdue: 'status-pill overdue',
};

const PILL_LABEL: Record<StepState, string> = {
  done: 'Done',
  'in-progress': 'In progress',
  todo: 'Not started',
  overdue: 'Overdue',
};

function formatDeedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Calm, plain time-to-FICA-review line. Never alarmist for a healthy review. */
function ficaLine(daysOut: number): { state: StepState; detail: string } {
  if (daysOut <= 0) {
    return { state: 'overdue', detail: `Review lapsed ${Math.abs(daysOut)} days ago` };
  }
  if (daysOut <= 30) {
    return { state: 'in-progress', detail: `Review due in ${daysOut} days` };
  }
  const months = Math.round(daysOut / 30);
  return {
    state: 'done',
    detail: months <= 1 ? 'Review current · next in about a month' : `Review current · next in about ${months} months`,
  };
}

interface JourneyStep {
  key: string;
  title: string;
  /** A settled detail string, or a skeleton shimmer while its query loads
   *  ("Checking…" read as system self-doubt — Paul's ruling, 2026-07-15). */
  detail: React.ReactNode;
  state: StepState;
  /** The pill's words when the state's default label would claim something
   *  untrue ("Not started" for a registration the documents cannot speak to). */
  pill?: string;
  /** Optional call-to-action link (label + route). */
  cta?: { label: string; to: string; params?: Record<string, string> };
}

/** The detail-line shimmer for a step whose backing query is still loading —
 *  shaped like the one-line detail text it replaces. */
function DetailSkeleton({ stepKey }: { stepKey: string }) {
  return <Skeleton testId={`journey-skeleton-${stepKey}`} width={130} height={12} />;
}

export interface RegistrationJourneyProps {
  trustId: string | undefined;
  /** Trust facts already loaded on the dashboard (from `useTrusts`). */
  trust: { registration?: string; deedDate?: string | null; registrationDate?: string | null } | undefined;
  /** Days until the next FICA review (from `/v1/dashboard/summary`). ABSENT on
   *  a trust with no initial FICA on file (TRS-03). */
  ficaDaysOut: number | undefined;
  /** TRS-03: `false` = the initial FICA is outstanding, so this rail shows the
   *  first verification as the step rather than a review that cannot be due. */
  initialFicaOnFile?: boolean | null;
}

export function RegistrationJourney({
  trustId,
  trust,
  ficaDaysOut,
  initialFicaOnFile,
}: RegistrationJourneyProps) {
  const docsQ = useTrustDocuments({ trustId });
  const assetsQ = useTrustAssets(trustId);

  // Nothing to chart without a trust in scope — the dashboard renders its own
  // "set up your trust" CTA in that case, so stay out of the way.
  if (!trustId) return null;

  const documents = docsQ.data?.documents ?? [];
  const docCount = documents.length;

  // ESTATE ownership model (2026-07-11 ruling): `person_owned` assets are
  // ESTATE-scoped, not trust-scoped, and must never distort this trust's n-of-m
  // count. `useTrustAssets` already queries by trust anchor
  // (`listForOwner('trust', trustId)`), so a person_owned row (no trust
  // anchor) can never actually appear here — this filter is belt-and-braces
  // defense-in-depth, not a fix for an observed bug. `earmarked_for_trust`
  // rows ARE trust-scoped (anchored to this trust, awaiting registration) and
  // correctly count toward the denominator — see the test pinning this.
  const assets = (assetsQ.data?.assets ?? []).filter((a) => a.ownershipState !== 'person_owned');
  const totalAssets = assets.length;
  const registeredAssets = assets.filter((a) => a.ownershipState === 'registered_to_trust').length;

  // The Master's registration evidence, and only that: the Letter of
  // Authority's date and a real IT number. The deed date is NOT used here; a
  // deed is signed before the Master registers anything (NEW-R12-DASH-
  // REGISTERED-UNTRUE printed "Registered" against the deed date).
  const loaDate = formatDeedDate(trust?.registrationDate);
  const registration =
    trust?.registration && trust.registration !== '—' && trust.registration !== PENDING_REGISTRATION
      ? trust.registration
      : null;

  const steps: JourneyStep[] = [];

  // 1 — Documents on file.
  steps.push({
    key: 'documents',
    title: 'Documents on file',
    detail: docsQ.isLoading ? (
      <DetailSkeleton stepKey="documents" />
    ) : docCount === 0 ? (
      'No documents uploaded yet'
    ) : (
      `${docCount} document${docCount === 1 ? '' : 's'} on file`
    ),
    state: docCount > 0 ? 'done' : 'todo',
    cta: trustId ? { label: 'View documents', to: '/app/trusts/$id/documents', params: { id: trustId } } : undefined,
  });

  // 2 — Master registration. "Registered" is said only with the Letter of
  // Authority's date on file; without it the step reports the state of the
  // file (lib/format/registration.ts: nothing on file is not evidence either
  // way), and offers the documents page to add the letter.
  steps.push(
    loaDate
      ? {
          key: 'registered',
          title: 'Master registration',
          detail: registration ? `Registered ${loaDate} · ${registration}` : `Registered ${loaDate}`,
          state: 'done',
        }
      : {
          key: 'registered',
          title: 'Master registration',
          detail: registration
            ? `${registration} · Letter of Authority not on file`
            : 'Letter of Authority not on file',
          state: 'todo',
          pill: 'Not on file',
          cta: { label: 'Add the letter', to: '/app/trusts/$id/documents', params: { id: trustId } },
        },
  );

  // 3 — Assets moved into the trust (n of m registered).
  const assetsDetail = assetsQ.isLoading ? (
    <DetailSkeleton stepKey="assets" />
  ) : totalAssets === 0 ? (
    'No assets recorded yet'
  ) : (
    `${registeredAssets} of ${totalAssets} registered to the trust`
  );
  const assetsState: StepState =
    totalAssets === 0
      ? 'todo'
      : registeredAssets >= totalAssets
        ? 'done'
        : 'in-progress';
  steps.push({
    key: 'assets',
    title: 'Assets into trust',
    detail: assetsDetail,
    state: assetsState,
    cta: trustId ? { label: 'View assets', to: '/app/trusts/$id/assets', params: { id: trustId } } : undefined,
  });

  // 4 — FICA (calm).
  // TRS-03: before a first verification there is no review cycle, so this rail
  // names the INITIAL FICA as the step. `ficaDaysOut` is absent on such a trust
  // (the backend withholds it), and `initialFicaOnFile === false` says which of
  // the two absences this is: work outstanding, or a date not yet read.
  if (initialFicaOnFile === false) {
    steps.push({
      key: 'fica',
      title: 'FICA verification',
      detail: 'No identity documents verified yet',
      state: 'todo',
      cta: trustId
        ? { label: 'Upload documents', to: '/app/trusts/$id/documents', params: { id: trustId } }
        : undefined,
    });
  } else {
    const fica = ficaLine(ficaDaysOut ?? 0);
    steps.push({
      key: 'fica',
      title: 'FICA review',
      detail: ficaDaysOut === undefined ? 'Review date pending' : fica.detail,
      state: ficaDaysOut === undefined ? 'in-progress' : fica.state,
    });
  }

  return (
    <section
      className="card"
      data-testid="registration-journey"
      aria-label="Registration progress"
      style={{ padding: '18px 22px' }}
    >
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        {/* Renamed from "Where your trust is" — 2026-08-22 meeting ruling. */}
        Trust status
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}
      >
        {steps.map((s) => (
          <div
            key={s.key}
            data-testid={`journey-step-${s.key}`}
            data-state={s.state}
            style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={PILL_CLASS[s.state]} data-testid={`journey-pill-${s.key}`}>
                {s.pill ?? PILL_LABEL[s.state]}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{s.title}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.45 }}>{s.detail}</div>
            {s.cta ? (
              <Link
                to={s.cta.to as never}
                {...(s.cta.params ? { params: s.cta.params as never } : {})}
                style={{
                  fontSize: 12,
                  color: 'var(--primary-700)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                {s.cta.label} →
              </Link>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}
